import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import type { Product } from '@/lib/api/product-api'
import { useCreateProduct } from '@/hooks/use-product-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** The scanned barcode that was not found */
    barcode: string
    /** Called with the newly created product so POS can add it to the order */
    onProductCreated?: (product: Product) => void
}

/**
 * Dialog shown when a barcode scan doesn't match any product.
 * Offers quick product creation with the barcode pre-filled.
 */
export function PosBarcodeNotFoundDialog({
    open,
    onOpenChange,
    barcode,
    onProductCreated,
}: Props) {
    const [showForm, setShowForm] = useState(false)
    const createProduct = useCreateProduct()

    const schema = z.object({
        sku: z.string().min(1, m.validation_required()),
        name: z.string().min(1, m.validation_required()),
        barcode: z.string().optional(),
        unit: z.string().min(1, m.validation_required()),
        unit_price: z.coerce.number().min(0),
    })

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            sku: '',
            name: '',
            barcode,
            unit: 'kg',
            unit_price: 0,
        },
    })

    // Reset form when dialog opens with new barcode
    useEffect(() => {
        if (open) {
            form.reset({
                sku: '',
                name: '',
                barcode,
                unit: 'kg',
                unit_price: 0,
            })
        }
    }, [open, barcode])

    const handleOpenChange = (v: boolean) => {
        if (!v) setShowForm(false)
        onOpenChange(v)
    }

    const handleSubmit = form.handleSubmit(async (data) => {
        try {
            const resp = await createProduct.mutateAsync({
                sku: data.sku,
                name: data.name,
                barcode: data.barcode || undefined,
                unit: data.unit,
                cost_price: 0,
                unit_price: data.unit_price,
            })
            if (resp.data) {
                toast.success(m.pos_barcode_added({ name: resp.data.name }))
                onProductCreated?.(resp.data)
            }
            handleOpenChange(false)
        } catch {
            toast.error(m.common_save_error())
        }
    })

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{m.pos_barcode_not_found_title()}</DialogTitle>
                    <DialogDescription>
                        {m.pos_barcode_not_found_desc({ barcode })}
                    </DialogDescription>
                </DialogHeader>

                {!showForm ? (
                    <div className="flex flex-col gap-2">
                        <Button onClick={() => setShowForm(true)}>
                            <Plus className="size-4 mr-1" />
                            {m.pos_create_product_quick()}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                        >
                            {m.common_close()}
                        </Button>
                    </div>
                ) : (
                    <FormProvider {...form}>
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <Field name="barcode" label={m.product_barcode()}>
                                <Input id="barcode" value={barcode} disabled />
                            </Field>

                            <Field name="sku" label={m.product_sku()} required>
                                <Input
                                    id="sku"
                                    autoFocus
                                    {...form.register('sku')}
                                />
                            </Field>

                            <Field
                                name="name"
                                label={m.product_name()}
                                required
                            >
                                <Input id="name" {...form.register('name')} />
                            </Field>

                            <div className="grid grid-cols-2 gap-2">
                                <Field
                                    name="unit_price"
                                    label={m.product_unit_price()}
                                >
                                    <Input
                                        id="unit_price"
                                        type="number"
                                        min={0}
                                        step="any"
                                        inputMode="decimal"
                                        {...form.register('unit_price')}
                                    />
                                </Field>
                                <Field
                                    name="unit"
                                    label={m.product_unit()}
                                    required
                                >
                                    <Input
                                        id="unit"
                                        {...form.register('unit')}
                                    />
                                </Field>
                            </div>

                            <div className="flex gap-2 pt-1">
                                <Button
                                    type="submit"
                                    className="flex-1"
                                    disabled={createProduct.isPending}
                                >
                                    {m.product_create()}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowForm(false)}
                                >
                                    {m.common_back()}
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                )}
            </DialogContent>
        </Dialog>
    )
}
